import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Table from "../../core/pagination/datatable";
import CommonFooter from "../../core/common/footer/commonFooter";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
// import { Period } from "../../core/common/selectOption/selectOption";
import Select from "react-select";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import {
  createWarranty,
  getAllWarranties,
  updateWarranty,
  deleteWarranty,
} from "../../api/warrantyApi"; // adjust path
import { toast } from "react-toastify";
import { getCurrentUserPerms, hasWrite, isCurrentUserAdmin } from "../../utils/perm";

const Warranty = () => {
  const user = useSelector((state) => state?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteWarranties =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "warranties");

  const [name, setName] = useState({ en: "", ar: "" });
  const [description, setDescription] = useState({ en: "", ar: "" });
  const [duration, setDuration] = useState("");
  const [period, setPeriod] = useState(""); // e.g., "days", "months"
  const [status, setStatus] = useState(true); // checkbox
  const [warrantyList, setWarrantyList] = useState([]);

  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState({ en: "", ar: "" });
  const [editDescription, setEditDescription] = useState({ en: "", ar: "" });
  const [editDuration, setEditDuration] = useState("");
  const [editPeriod, setEditPeriod] = useState("");
  const [editStatus, setEditStatus] = useState(true);

  const [deleteId, setDeleteId] = useState(null);

  //get list
  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getAllWarranties();
        setWarrantyList(result);
      } catch (err) {
        // toast.error(err.message || "Failed to fetch warranty list");
        toast.error(`❌ ${window.translate("Failed to fetch warranty list")}`);
      }
    };

    fetchData();
  }, []);

  const handleAddWarranty = async () => {
    if (!canWriteWarranties) {
      return toast.error(`❌ ${window.translate("You don't have permission to modify warranties")}`);
    }
    // Validations
    if (!name.en.trim() || !name.ar.trim()) {
      // return toast.error("Warranty name in both languages is required");
      return toast.error(`❌ ${window.translate("Warranty name in both languages is required")}`);
    }

    if (!description.en.trim() || !description.ar.trim()) {
      // return toast.error("Description in both languages is required");
      return toast.error(`❌ ${window.translate("Description in both languages is required")}`);
    }

    if (!duration || isNaN(duration)) {
      // return toast.error("Valid numeric duration is required");
      return toast.error(`❌ ${window.translate("Valid numeric duration is required")}`);
    }

    if (!period) {
      // return toast.error("Please select a period");
      return toast.error(`❌ ${window.translate("Please select a period")}`);
    }

    const payload = {
      name,
      description,
      duration,
      period,
      status: status ? "on" : "off",
    };

    try {
      await createWarranty(payload);
      // toast.success("Warranty created successfully");
      toast.success(` ${window.translate("Warranty created successfully")}`);

      // Reset form
      setName({ en: "", ar: "" });
      setDescription({ en: "", ar: "" });
      setDuration("");
      setPeriod("");
      setStatus(true);

      // Close modal
      document.getElementById("close-add-warranty-btn")?.click();
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      // toast.error(err.response?.data?.message || "Failed to create warranty");
      toast.error(`❌ ${window.translate("Failed to create warranty")}`);
    }
  };

  const handleEditWarranty = async () => {
    if (!canWriteWarranties) {
      return toast.error(`❌ ${window.translate("You don't have permission to modify warranties")}`);
    }
    if (!editName.en.trim() || !editName.ar.trim()) {
      // return toast.error("Warranty name in both languages is required");
      return toast.error(`❌ ${window.translate("Warranty name in both languages is required")}`);
    }
    if (!editDescription.en.trim() || !editDescription.ar.trim()) {
      // return toast.error("Description in both languages is required");
    return toast.error(`❌ ${window.translate("Description in both languages is required")}`);
  }

    if (!editDuration || isNaN(editDuration)) {
      // return toast.error("Valid numeric duration is required");
      return toast.error(`❌ ${window.translate("Valid numeric duration is required")}`);
    }
    if (!editPeriod) {
      // return toast.error("Please select a period");
      return toast.error(`❌ ${window.translate("Please select a period")}`);
    }

    const payload = {
      name: editName,
      description: editDescription,
      duration: editDuration,
      period: editPeriod,
      status: editStatus ? "on" : "off",
    };

    try {
      await updateWarranty(editId, payload);
      // toast.success("Warranty updated successfully");
      toast.success(`✅ ${window.translate("Warranty updated successfully")}`);

      // Refresh warranty list
      const refreshed = await getAllWarranties();
      setWarrantyList(refreshed);

      // Reset
      setEditId(null);
      setEditName({ en: "", ar: "" });
      setEditDescription({ en: "", ar: "" });
      setEditDuration("");
      setEditPeriod("");
      setEditStatus(true);

      // Close modal
      document.getElementById("close-edit-warranty-btn")?.click();
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      toast.error(`${window.translate("Failed to update warranty")}`);
    }
  };

  const handleDeleteWarranty = async () => {
    if (!canWriteWarranties) {
      return toast.error(`❌ ${window.translate("You don't have permission to modify warranties")}`);
    }
    if (!deleteId) return toast.error(`${window.translate("No warranty selected for deletion")}`);

    try {
      await deleteWarranty(deleteId);
      // toast.success("Warranty deleted successfully");
      toast.success(` ${window.translate("Warranty deleted successfully")}`);

      // Refresh warranty list
      const refreshed = await getAllWarranties();
      setWarrantyList(refreshed);

      setDeleteId(null); // reset
      document.getElementById("close-delete-modal-btn")?.click();
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      toast.error(`${window.translate("Failed to delete warranty")}`);
    }
  };

  const Period = [
    { value: "days", label: "Days" },
    { value: "weeks", label: "Weeks" },
    { value: "months", label: "Months" },
    { value: "years", label: "Years" },
  ];

  const columns = [
    {
      title: window.translate("Name"),
      dataIndex: "name",
      sorter: (a, b) => a.name.length - b.name.length,
      width: "10px",
    },
    {
      title: window.translate("Description"),
      dataIndex: "description",
      sorter: (a, b) => a.description.length - b.description.length,
      width: "10px",
    },

    {
      title: window.translate("Duration"),
      dataIndex: "duration",
      sorter: (a, b) => a.duration.length - b.duration.length,
      width: "10px",
    },

    {
      title: window.translate("Period"),
      dataIndex: "period",
      sorter: (a, b) => a.period.localeCompare(b.period),
      width: "10px",
    },

    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (text) => (
        <span className="badge table-badge bg-success fw-medium fs-10">
          {text}
        </span>
      ),
      sorter: (a, b) => a.status.length - b.status.length,
    },

    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) =>
        canWriteWarranties ? (
          <div className="action-table-data">
            <div className="edit-delete-action">
              <Link
                className="me-2 p-2"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#edit-units"
                onClick={() => {
                  setEditId(record._id);
                  setEditName(record.fullName || { en: "", ar: "" });
                  setEditDescription(
                    record.fullDescription || { en: "", ar: "" }
                  );
                  setEditDuration(record.duration);
                  setEditPeriod(record.period);
                  setEditStatus(record.statusRaw === "on");
                }}
              >
                <i data-feather="edit" className="feather-edit"></i>
              </Link>
              <Link
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                className="p-2"
                to="#"
                onClick={() => setDeleteId(record._id)}
              >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            </div>
          </div>
        ) : null,
    },
  ];

  // const [values, setValue] = useState();
  // function onChange(e) {
  //   setValue(e.target.value);
  // }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("Warranties")}</h4>
                <h6>{window.translate("Manage your warranties")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            {canWriteWarranties && (
              <div className="page-btn">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add-units"
                >
                  <i className="ti ti-circle-plus me-2 ms-2"></i> {window.translate("Add Warranty")}
                </Link>
              </div>
            )}
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("status")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("active")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        In{window.translate("active")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                {warrantyList.length > 0 ? (
                  <Table columns={columns} dataSource={warrantyList} />
                ) : (
                  <div className="text-center py-4">
                    No warranty records found.
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* /product list */}
        </div>
        <CommonFooter />
      </div>

      {/* Add Warranty */}
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("addWarranty")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close bg-danger text-white fs-16"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body">
                  <form>
                    {/* Name EN */}
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Warranty Name")} (EN){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={name.en}
                        onChange={(e) =>
                          setName({ ...name, en: e.target.value })
                        }
                      />
                    </div>

                    {/* Name AR */}
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Warranty Name")} (AR){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={name.ar}
                        onChange={(e) =>
                          setName({ ...name, ar: e.target.value })
                        }
                      />
                    </div>

                    {/* Duration and Period */}
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Duration")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Period")} <span className="text-danger">*</span>
                          </label>
                          <Select
                            classNamePrefix="react-select"
                            options={Period}
                            placeholder="Choose"
                            value={Period.find((opt) => opt.value === period)}
                            onChange={(e) => setPeriod(e.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Description")} (EN) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={description.en}
                        onChange={(e) =>
                          setDescription({ ...description, en: e.target.value })
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Description")} (AR) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={description.ar}
                        onChange={(e) =>
                          setDescription({ ...description, ar: e.target.value })
                        }
                      />
                    </div>

                    {/* Status */}
                    <div className="mb-0">
                      <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                        <span className="status-label">
                          {window.translate("status")}
                        </span>
                        <input
                          type="checkbox"
                          id="user2"
                          className="check"
                          checked={status}
                          onChange={(e) => setStatus(e.target.checked)}
                        />
                        <label htmlFor="user2" className="checktoggle" />
                      </div>
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                    id="close-add-warranty-btn"
                  >
                    {window.translate("Cancel")}
                  </button>
                  <button
                    to="#"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    data-bs-dismiss="modal"
                    onClick={handleAddWarranty}
                  >
                    {window.translate("Add Warranty")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Add Warranty */}
      {/* Edit Warranty */}
      <div className="modal fade" id="edit-units">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("Edit Warrranty")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close bg-danger text-white fs-16"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    id="close-edit-warranty-btn"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body">
                  <form>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Warranty")} EN<span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editName.en}
                        onChange={(e) =>
                          setEditName((prev) => ({
                            ...prev,
                            en: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {window.translate("Warranty")} AR<span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editName.ar}
                        onChange={(e) =>
                          setEditName((prev) => ({
                            ...prev,
                            ar: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Duration")}<span className="text-danger ms-1">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editDuration}
                            onChange={(e) => setEditDuration(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Period")}<span className="text-danger ms-1">*</span>
                          </label>
                          <Select
                            classNamePrefix="react-select"
                            options={Period}
                            placeholder="Choose"
                            value={Period.find(
                              (opt) => opt.value === editPeriod
                            )}
                            onChange={(e) => setEditPeriod(e.value)}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Description")} EN
                            <span className="text-danger ms-1">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editDescription.en}
                            onChange={(e) =>
                              setEditDescription((prev) => ({
                                ...prev,
                                en: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">
                            {window.translate("Description")} AR
                            <span className="text-danger ms-1">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editDescription.ar}
                            onChange={(e) =>
                              setEditDescription((prev) => ({
                                ...prev,
                                ar: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mb-0">
                      <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                        <span className="status-label">
                          {window.translate("status")}
                        </span>
                        <input
                          type="checkbox"
                          id="user3"
                          className="check"
                          checked={editStatus}
                          onChange={(e) => setEditStatus(e.target.checked)}
                        />
                        <label htmlFor="user3" className="checktoggle" />
                      </div>
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    {window.translate("Cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    onClick={handleEditWarranty}
                    data-bs-dismiss="modal"
                    id="close-edit-warranty-btn"
                  >
                    {window.translate("Save Changes")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Edit Warranty */}
      <CommonDeleteModal onConfirm={handleDeleteWarranty} />
    </>
  );
};

export default Warranty;
